'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { leadListMachine } from './machines/leadListMachine'
import type { Lead, LeadActivity, Pipeline, LeadFilters, CRMStats } from './types'

export type LeadListMachineApi = ReturnType<typeof useMachine<typeof leadListMachine>>

const LeadListMachineContext = createContext<LeadListMachineApi | null>(null)

export function LeadListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(leadListMachine)
  return <LeadListMachineContext.Provider value={api}>{children}</LeadListMachineContext.Provider>
}

export function useLeadListMachine() {
  const ctx = useContext(LeadListMachineContext)
  if (!ctx) {
    throw new Error('useLeadListMachine must be used within LeadListMachineProvider')
  }
  return ctx
}

export function useLeadListMachineOptional() {
  return useContext(LeadListMachineContext)
}

/**
 * Adapter: returns store-compatible interface.
 */
export function useLeadListStoreCompat() {
  const machineApi = useLeadListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const ctx = state.context

    return {
      leads: ctx.leads,
      selectedLead: ctx.selectedLead,
      activities: ctx.activities,
      pipelines: ctx.pipelines,
      activePipelineId: ctx.activePipelineId,
      stats: ctx.stats,
      filters: ctx.filters,
      totalLeads: ctx.totalLeads,
      isLoading: ctx.isLoading,
      setLeads: (leads: Lead[], total: number) => send({ type: 'SET_LEADS', leads, total }),
      addLead: (lead: Lead) => send({ type: 'ADD_LEAD', lead }),
      updateLead: (leadId: string, data: Partial<Lead>) =>
        send({ type: 'UPDATE_LEAD', leadId, data }),
      removeLead: (leadId: string) => send({ type: 'REMOVE_LEAD', leadId }),
      setSelectedLead: (lead: Lead | null) => send({ type: 'SET_SELECTED_LEAD', lead }),
      moveLeadToStage: (leadId: string, status: Lead['status']) =>
        send({ type: 'MOVE_LEAD_TO_STAGE', leadId, status }),
      setActivities: (leadId: string, activities: LeadActivity[]) =>
        send({ type: 'SET_ACTIVITIES', leadId, activities }),
      addActivity: (leadId: string, activity: LeadActivity) =>
        send({ type: 'ADD_ACTIVITY', leadId, activity }),
      setPipelines: (pipelines: Pipeline[]) => send({ type: 'SET_PIPELINES', pipelines }),
      setActivePipeline: (pipelineId: string) => send({ type: 'SET_ACTIVE_PIPELINE', pipelineId }),
      setStats: (stats: CRMStats) => send({ type: 'SET_STATS', stats }),
      setFilters: (filters: Partial<LeadFilters>) => send({ type: 'SET_FILTERS', filters }),
      resetFilters: () => send({ type: 'RESET_FILTERS' }),
      setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
    }
  }, [machineApi])
}

export function useLeadActions() {
  const machineApi = useLeadListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setLeads: (leads: Lead[], total: number) => send({ type: 'SET_LEADS', leads, total }),
    addLead: (lead: Lead) => send({ type: 'ADD_LEAD', lead }),
    updateLead: (leadId: string, data: Partial<Lead>) =>
      send({ type: 'UPDATE_LEAD', leadId, data }),
    removeLead: (leadId: string) => send({ type: 'REMOVE_LEAD', leadId }),
    setSelectedLead: (lead: Lead | null) => send({ type: 'SET_SELECTED_LEAD', lead }),
    moveLeadToStage: (leadId: string, status: Lead['status']) =>
      send({ type: 'MOVE_LEAD_TO_STAGE', leadId, status }),
    setActivities: (leadId: string, activities: LeadActivity[]) =>
      send({ type: 'SET_ACTIVITIES', leadId, activities }),
    addActivity: (leadId: string, activity: LeadActivity) =>
      send({ type: 'ADD_ACTIVITY', leadId, activity }),
    setPipelines: (pipelines: Pipeline[]) => send({ type: 'SET_PIPELINES', pipelines }),
    setActivePipeline: (pipelineId: string) => send({ type: 'SET_ACTIVE_PIPELINE', pipelineId }),
    setStats: (stats: CRMStats) => send({ type: 'SET_STATS', stats }),
    setFilters: (filters: Partial<LeadFilters>) => send({ type: 'SET_FILTERS', filters }),
    resetFilters: () => send({ type: 'RESET_FILTERS' }),
    setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
  }), [send])
}

export function useLeads() {
  const compat = useLeadListStoreCompat()
  return compat.leads
}

export function useSelectedLead() {
  const compat = useLeadListStoreCompat()
  return compat.selectedLead
}

export function useLeadActivities(leadId: string) {
  const compat = useLeadListStoreCompat()
  return compat.activities[leadId] || []
}

export function usePipelines() {
  const compat = useLeadListStoreCompat()
  return compat.pipelines
}

export function useActivePipeline() {
  const compat = useLeadListStoreCompat()
  return compat.pipelines.find((p) => p.id === compat.activePipelineId) ?? null
}

export function useCRMStats() {
  const compat = useLeadListStoreCompat()
  return compat.stats
}

export function useLeadFilters() {
  const compat = useLeadListStoreCompat()
  return compat.filters
}

export function useLeadsByStatus() {
  const compat = useLeadListStoreCompat()
  return useMemo(() => {
    const grouped: Record<string, Lead[]> = {}
    compat.leads.forEach((lead) => {
      if (!grouped[lead.status]) grouped[lead.status] = []
      grouped[lead.status]!.push(lead)
    })
    return grouped
  }, [compat.leads])
}
